import { MazeCanvas } from '@/components/custom/maze/maze-canvas';
import {
  DemoTutorialBlurOverlay,
  DemoTutorialHandOverlay,
  DemoTutorialScrim,
  DemoTutorialTapLayer,
  DemoTutorialTapPrompt,
} from '@/components/molecules/demo-tutorial-overlay';
import { MazePlayTopGradient } from '@/components/molecules/maze-play-top-gradient';
import { MazeControls } from '@/components/organisms/maze-controls';
import { MazeHud } from '@/components/organisms/maze-hud';
import { MAZE_FLIP_PHASE, type MazeFlipPhase } from '@/constants/maze';
import { useDemoStore } from '@/store/demo';
import { APP_COLOR } from '@/theme/color';
import { MazeGamePhase } from '@/types/maze-phase';
import {
  isDemoTutorialActive,
  isDemoTutorialHighlightControlsStep,
} from '@/utils/maze/demo-tutorial';
import { isMazePlayModeDemo, isMazePlayModePreview, MazePlayMode } from '@/utils/maze/maze-play-mode';
import { motion, useAnimationControls } from 'motion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface MazeTemplateProps {
  readonly phase: MazeGamePhase;
  readonly level: number;
  readonly timer: number;
  readonly playMode: MazePlayMode;
  readonly soundEffectsEnabled: boolean;
  readonly onToggleSoundEffects: (enabled: boolean) => void;
  readonly controlsEnabled: boolean;
  readonly onToggleControls: (enabled: boolean) => void;
  readonly onStartFresh: () => void | Promise<void>;
  readonly isStartFreshPending?: boolean;
  readonly onDone?: () => void;
  readonly flipPhase?: MazeFlipPhase;
  readonly levelCompleteBurst?: boolean;
  readonly stopAnimation?: boolean;
  readonly onStopAnimationToggle?: () => void;
  readonly onSkip?: () => void;
  readonly isSkipPending?: boolean;
  readonly isTimerEnabled?: boolean;
  readonly lives?: number;
}

const FLIP_HALF_S = 0.42;

export function MazeTemplate({
  phase,
  level,
  timer,
  playMode,
  soundEffectsEnabled,
  onToggleSoundEffects,
  controlsEnabled,
  onToggleControls,
  onStartFresh,
  isStartFreshPending,
  onDone,
  flipPhase = MAZE_FLIP_PHASE.IDLE,
  levelCompleteBurst = false,
  stopAnimation = false,
  onStopAnimationToggle,
  onSkip,
  isSkipPending = false,
  isTimerEnabled = true,
  lives,
}: MazeTemplateProps) {
  const { t } = useTranslation();
  const flipControls = useAnimationControls();
  // NOTE: fps is for testing purposes only
  // const fps = useFps();
  const isDemo = isMazePlayModeDemo(playMode);
  const currentLevelIndex = useDemoStore((s) => s.currentLevelIndex);
  const demoLevel0TutorialCompleted = useDemoStore((s) => s.demoLevel0TutorialCompleted);
  const demoTutorialStep = useDemoStore((s) => s.demoTutorialStep);
  const startDemoTutorial = useDemoStore((s) => s.startDemoTutorial);
  const demoLevel = useDemoStore((s) =>
    isDemo ? s.demoSession?.levels[s.currentLevelIndex] : null,
  );
  const demoLevelId = demoLevel?.levelId ?? null;

  useEffect(() => {
    if (flipPhase === MAZE_FLIP_PHASE.FOLDING) {
      void flipControls.start({
        rotateY: -90,
        transition: { duration: FLIP_HALF_S, ease: [0.4, 0, 1, 1] },
      });
    } else if (flipPhase === MAZE_FLIP_PHASE.UNFOLDING) {
      flipControls.set({ rotateY: 90 });
      void flipControls.start({
        rotateY: 0,
        transition: { duration: FLIP_HALF_S, ease: [0, 0, 0.6, 1] },
      });
    }
  }, [flipControls, flipPhase]);

  useEffect(() => {
    if (
      !isDemo ||
      phase !== MazeGamePhase.PLAYING ||
      currentLevelIndex !== 0 ||
      demoLevel0TutorialCompleted
    ) {
      return;
    }
    startDemoTutorial();
  }, [
    currentLevelIndex,
    demoLevel0TutorialCompleted,
    demoLevelId,
    isDemo,
    phase,
    startDemoTutorial,
  ]);

  const isPreviewWin = isMazePlayModePreview(playMode) && phase === MazeGamePhase.WIN;
  if (phase !== MazeGamePhase.PLAYING && !isPreviewWin) {
    return null;
  }

  const tutorialActive = isDemo && isDemoTutorialActive(demoTutorialStep);

  const tutorialShouldBeActive =
    isDemo &&
    currentLevelIndex === 0 &&
    !demoLevel0TutorialCompleted &&
    demoTutorialStep !== 'done';
  const highlightControls = isDemo && isDemoTutorialHighlightControlsStep(demoTutorialStep);
  const effectiveHighlightControls = highlightControls && controlsEnabled;
  const dimHud = false;
  const dimCanvas = false;

  const cntrlOpacityFn = () => {
    if (!controlsEnabled) {
      return 0;
    }
    if (tutorialActive && !effectiveHighlightControls) {
      return 0.2;
    }
    return 1;
  };

  return (
    <main className="relative min-h-dvh w-full overflow-x-hidden py-[32px]">
      <MazePlayTopGradient />
      {tutorialShouldBeActive ? <DemoTutorialBlurOverlay /> : null}
      {tutorialActive ? <DemoTutorialTapLayer /> : null}
      {tutorialActive ? <DemoTutorialHandOverlay /> : null}
      {tutorialActive ? <DemoTutorialTapPrompt /> : null}
      <div className="mx-auto flex w-full max-w-[600px] h-[calc(100dvh_-_64px)] flex-col items-center justify-between gap-2 px-[15px]">
        <div className="relative w-full max-w-[465px]">
          <DemoTutorialScrim show={dimHud} className="absolute inset-0 z-[8] rounded-xl" />
          <MazeHud
            playMode={playMode}
            level={level}
            timer={timer}
            soundEffectsEnabled={soundEffectsEnabled}
            onToggleSoundEffects={onToggleSoundEffects}
            controlsEnabled={controlsEnabled}
            onToggleControls={onToggleControls}
            onStartFresh={onStartFresh}
            isStartFreshPending={isStartFreshPending}
            onDone={onDone}
            tutorialUiDisabled={tutorialActive}
            levelCompleteBurst={levelCompleteBurst}
            isLevelTransition={flipPhase !== MAZE_FLIP_PHASE.IDLE}
            stopAnimation={stopAnimation}
            onStopAnimationToggle={onStopAnimationToggle}
            onSkip={onSkip}
            isSkipPending={isSkipPending}
            isTimerEnabled={isTimerEnabled}
            lives={lives}
          />
          <h2
            className="font-semibold text-[18px] leading-[32px] h-[32px] tracking-[0.16px] text-center text-[#9E9BA4] z-[9] mt-[10px] [@media(max-height:620px)]:hidden"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {levelCompleteBurst && t('win.mazeCleared')}
          </h2>
        </div>
        <div
          className={`relative h-[50vh] w-full max-w-md min-h-0 shrink-0 mt-[-52px] ${tutorialActive ? ' z-[99]' : ''}`}
          style={{ perspective: '1400px' }}
        >
          <motion.div
            animate={flipControls}
            className="relative h-full w-full"
            style={{ transformStyle: 'preserve-3d' }}
          >
            <DemoTutorialScrim show={dimCanvas} className="absolute inset-0 z-[8] rounded-2xl" />
            <div className="absolute inset-0 min-h-0">
              <MazeCanvas playMode={playMode} inputBlocked={flipPhase !== MAZE_FLIP_PHASE.IDLE || phase === MazeGamePhase.WIN} />
            </div>
          </motion.div>
        </div>
        <div
          className={`relative flex w-full shrink-0 flex-col items-center pt-[28px]${effectiveHighlightControls ? ' z-[99]' : ' z-20'}`}
        >
          <MazeControls
            inputDisabled={tutorialActive || phase === MazeGamePhase.WIN}
            highlightControls={effectiveHighlightControls}
            isVisible={controlsEnabled}
          />
          <p
            className="absolute left-1/2 bottom-[120px] z-16 max-w-[370px] overflow-hidden whitespace-nowrap rounded-full border px-[22px] py-[10px] text-center text-[12px] font-bold capitalize tracking-[0.16px] pointer-events-none -translate-x-1/2"
            style={{
              background: APP_COLOR.background,
              borderColor: effectiveHighlightControls
                ? APP_COLOR.infoSoft60
                : APP_COLOR.accentSoft40,
              color: effectiveHighlightControls ? APP_COLOR.infoBright : APP_COLOR.accent,
              animation: effectiveHighlightControls ? 'pulse 2s ease-in-out infinite' : undefined,
              opacity: cntrlOpacityFn(),
              visibility: controlsEnabled ? 'visible' : 'hidden',
            }}
            aria-hidden={!controlsEnabled}
          >
            {t('controls.hint')}
          </p>
        </div>
      </div>
    </main>
  );
}
